import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Plus, Send, CheckCircle, Clock, Circle, AlertTriangle, DollarSign, FileText, Loader2, Receipt, Users, Trash2, UserPlus, Upload, Activity, Download } from 'lucide-react';
import api, { LOCATIONS, formatZARFull } from '@/lib/api';
import { toast } from 'sonner';

const STAGE_STATUS = {
  not_started: { label: 'Not Started', icon: Circle, color: 'text-brand-dark/30', bg: 'bg-gray-100' },
  in_progress: { label: 'In Progress', icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
  completed: { label: 'Completed', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
};

const IMPACT_COLORS = { cost: 'bg-red-50 text-red-700 border-red-200', time: 'bg-amber-50 text-amber-700 border-amber-200', none: 'bg-gray-50 text-gray-600 border-gray-200' };
const MILESTONE_STATUS = { planned: 'bg-gray-100 text-gray-600', invoiced: 'bg-blue-50 text-blue-700', paid: 'bg-green-50 text-green-700' };

export default function StudioWorkspace({ projectId, onBack }) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('stages');
  const [noteText, setNoteText] = useState('');
  const [scopeForm, setScopeForm] = useState({ description: '', impact: 'none', impact_detail: '', requested_by: 'Client' });
  const [milestoneForm, setMilestoneForm] = useState({ description: '', amount: '', stage: '', due_date: '' });
  const [showScopeForm, setShowScopeForm] = useState(false);
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [billingReadiness, setBillingReadiness] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ reference: '', amount: '', date_issued: '', milestone_id: '', stage_id: '', notes: '' });
  const [suggestedProfs, setSuggestedProfs] = useState([]);
  const [showTeamAdd, setShowTeamAdd] = useState(false);
  const [teamNotes, setTeamNotes] = useState('');
  const [bimSignals, setBimSignals] = useState(null);
  const [bimSnapshots, setBimSnapshots] = useState([]);
  const [showBimForm, setShowBimForm] = useState(false);
  const [bimForm, setBimForm] = useState({ declared_stage: '', label: 'checkpoint', notes: '', gfa: '', levels: '', room_count: '', element_count: '', wall_area: '', roof_area: '', glazing_area: '', revit_file: '', revit_version: '' });

  useEffect(() => { fetchProject(); }, [projectId]); // eslint-disable-line

  const fetchProject = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/studio/projects/${projectId}`);
      setProject(data);
    } catch { toast.error('Failed to load project'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === 'billing' && projectId) { fetchBilling(); fetchInvoices(); }
    if (tab === 'team' && projectId) fetchSuggested();
    if (tab === 'bim' && projectId) { fetchBimSignals(); fetchBimSnapshots(); }
  }, [tab, projectId]); // eslint-disable-line

  // Read-only fetches: log failures so we can debug session expiry / network
  // issues rather than silently rendering empty panels.
  const fetchBilling = async () => {
    try { const { data } = await api.get(`/studio/projects/${projectId}/billing-readiness`); setBillingReadiness(data); }
    catch (e) { console.error('billing-readiness fetch failed', e); }
  };
  const fetchInvoices = async () => {
    try { const { data } = await api.get(`/studio/projects/${projectId}/invoices`); setInvoices(data); }
    catch (e) { console.error('invoices fetch failed', e); }
  };
  const fetchSuggested = async () => {
    try { const { data } = await api.get(`/studio/projects/${projectId}/suggested-professionals`); setSuggestedProfs(data); }
    catch (e) { console.error('suggested-professionals fetch failed', e); }
  };
  const fetchBimSignals = async () => {
    try { const { data } = await api.get(`/studio/projects/${projectId}/bim-signals`); setBimSignals(data); }
    catch (e) { console.error('bim-signals fetch failed', e); }
  };
  const fetchBimSnapshots = async () => {
    try { const { data } = await api.get(`/studio/projects/${projectId}/bim-snapshots`); setBimSnapshots(data); }
    catch (e) { console.error('bim-snapshots fetch failed', e); }
  };
  const uploadSnapshot = async () => {
    if (!bimForm.declared_stage || !bimForm.gfa) { toast.error('Declared stage and GFA are required'); return; }
    try {
      await api.post(`/studio/projects/${projectId}/bim-snapshots`, {
        declared_stage: bimForm.declared_stage, label: bimForm.label, notes: bimForm.notes,
        metrics: {
          gfa: Number(bimForm.gfa) || 0, levels: Number(bimForm.levels) || 0,
          room_count: Number(bimForm.room_count) || 0, element_count: Number(bimForm.element_count) || 0,
          wall_area: Number(bimForm.wall_area) || 0, roof_area: Number(bimForm.roof_area) || 0,
          glazing_area: Number(bimForm.glazing_area) || 0, revit_file: bimForm.revit_file, revit_version: bimForm.revit_version,
        },
      });
      setBimForm({ declared_stage: '', label: 'checkpoint', notes: '', gfa: '', levels: '', room_count: '', element_count: '', wall_area: '', roof_area: '', glazing_area: '', revit_file: '', revit_version: '' });
      setShowBimForm(false);
      fetchBimSignals(); fetchBimSnapshots();
      toast.success('Snapshot uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to upload'); }
  };

  const updateStage = async (stageId, updates) => {
    try {
      await api.patch(`/studio/projects/${projectId}/stages/${stageId}`, updates);
      fetchProject();
    } catch { toast.error('Failed to update stage'); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/studio/projects/${projectId}/notes`, { content: noteText });
      setNoteText('');
      fetchProject();
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
  };

  const addScopeChange = async () => {
    if (!scopeForm.description) return;
    try {
      await api.post(`/studio/projects/${projectId}/scope-changes`, scopeForm);
      setScopeForm({ description: '', impact: 'none', impact_detail: '', requested_by: 'Client' });
      setShowScopeForm(false);
      fetchProject();
      toast.success('Scope change logged');
    } catch { toast.error('Failed to log scope change'); }
  };

  const addMilestone = async () => {
    if (!milestoneForm.description) return;
    try {
      await api.post(`/studio/projects/${projectId}/milestones`, { ...milestoneForm, amount: Number(milestoneForm.amount) || 0 });
      setMilestoneForm({ description: '', amount: '', stage: '', due_date: '' });
      setShowMilestoneForm(false);
      fetchProject();
      toast.success('Milestone added');
    } catch { toast.error('Failed to add milestone'); }
  };

  const updateMilestone = async (milestoneId, status) => {
    try {
      await api.patch(`/studio/projects/${projectId}/milestones/${milestoneId}`, { status });
      fetchProject();
    } catch { toast.error('Failed to update'); }
  };

  const addInvoice = async () => {
    if (!invoiceForm.reference) return;
    try {
      await api.post(`/studio/projects/${projectId}/invoices`, { ...invoiceForm, amount: Number(invoiceForm.amount) || 0 });
      setInvoiceForm({ reference: '', amount: '', date_issued: '', milestone_id: '', stage_id: '', notes: '' });
      setShowInvoiceForm(false);
      fetchInvoices(); fetchBilling();
      toast.success('Invoice logged');
    } catch { toast.error('Failed to log invoice'); }
  };

  const updateInvoiceStatus = async (invoiceId, status) => {
    try { await api.patch(`/studio/invoices/${invoiceId}`, { status }); fetchInvoices(); } catch { toast.error('Failed to update'); }
  };

  const deleteInvoice = async (invoiceId) => {
    try { await api.delete(`/studio/invoices/${invoiceId}`); fetchInvoices(); fetchBilling(); toast.success('Invoice removed'); }
    catch (e) { console.error('invoice delete failed', e); toast.error('Failed to remove invoice'); }
  };

  const linkProfessional = async (prof) => {
    try {
      await api.post(`/studio/projects/${projectId}/team`, {
        professional_id: prof.professional_id, name: prof.name, discipline: prof.discipline, role_notes: teamNotes,
      });
      setTeamNotes('');
      fetchProject();
      toast.success(`${prof.name} linked to project`);
    } catch { toast.error('Failed to link'); }
  };

  const unlinkProfessional = async (profId) => {
    try { await api.delete(`/studio/projects/${projectId}/team/${profId}`); fetchProject(); toast.success('Removed from team'); }
    catch (e) { console.error('team unlink failed', e); toast.error('Failed to remove'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-dark/30" /></div>;
  if (!project) return <div className="text-center py-20 text-brand-dark/40">Project not found.</div>;

  const completedStages = project.stages?.filter(s => s.status === 'completed').length || 0;
  const totalStages = project.stages?.length || 6;
  const totalFee = project.fee_structure?.total_fee || 0;
  const totalInvoiced = project.milestones?.filter(m => m.status === 'invoiced' || m.status === 'paid').reduce((sum, m) => sum + (m.amount || 0), 0) || 0;
  const totalPaid = project.milestones?.filter(m => m.status === 'paid').reduce((sum, m) => sum + (m.amount || 0), 0) || 0;

  return (
    <div data-testid="studio-workspace">
      {/* Header */}
      <Button variant="ghost" onClick={onBack} className="mb-4 text-sm" data-testid="workspace-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Projects
      </Button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="font-heading text-xl font-medium" data-testid="workspace-title">{project.name}</h2>
          <p className="text-brand-dark/50 font-body text-sm">{project.client_name} — {project.client_email}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs capitalize">{project.status}</Badge>
            <span className="text-xs text-brand-dark/40 font-body">
              {LOCATIONS.find(l => l.value === project.location)?.label || project.location} · {project.project_type?.replace('_', ' ')}
            </span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Progress</p>
          <p className="font-heading text-lg font-medium text-brand-dark">{completedStages}/{totalStages} stages</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-sm border border-black/10 p-4">
          <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Total Fee</p>
          <p className="font-heading text-lg font-medium text-brand-dark">{totalFee ? formatZARFull(totalFee) : 'Not set'}</p>
        </div>
        <div className="bg-white rounded-sm border border-black/10 p-4">
          <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Invoiced</p>
          <p className="font-heading text-lg font-medium text-blue-600">{formatZARFull(totalInvoiced)}</p>
        </div>
        <div className="bg-white rounded-sm border border-black/10 p-4">
          <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Collected</p>
          <p className="font-heading text-lg font-medium text-green-600">{formatZARFull(totalPaid)}</p>
        </div>
        <div className="bg-white rounded-sm border border-black/10 p-4">
          <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Scope Changes</p>
          <p className="font-heading text-lg font-medium text-brand-dark">{project.scope_changes?.length || 0}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-black/10 rounded-sm h-10">
          <TabsTrigger value="stages" className="rounded-sm text-xs font-body uppercase tracking-wider px-4" data-testid="workspace-tab-stages">Stages</TabsTrigger>
          <TabsTrigger value="scope" className="rounded-sm text-xs font-body uppercase tracking-wider px-4" data-testid="workspace-tab-scope">Scope Log</TabsTrigger>
          <TabsTrigger value="fees" className="rounded-sm text-xs font-body uppercase tracking-wider px-4" data-testid="workspace-tab-fees">Fees</TabsTrigger>
          <TabsTrigger value="billing" className="rounded-sm text-xs font-body uppercase tracking-wider px-4" data-testid="workspace-tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="team" className="rounded-sm text-xs font-body uppercase tracking-wider px-4" data-testid="workspace-tab-team">Team</TabsTrigger>
          <TabsTrigger value="bim" className="rounded-sm text-xs font-body uppercase tracking-wider px-4" data-testid="workspace-tab-bim">BIM</TabsTrigger>
          <TabsTrigger value="context" className="rounded-sm text-xs font-body uppercase tracking-wider px-4" data-testid="workspace-tab-context">Context</TabsTrigger>
          <TabsTrigger value="notes" className="rounded-sm text-xs font-body uppercase tracking-wider px-4" data-testid="workspace-tab-notes">Notes</TabsTrigger>
        </TabsList>

        {/* STAGES */}
        <TabsContent value="stages" className="mt-4">
          <div className="space-y-3" data-testid="stages-list">
            {project.stages?.map((stage, i) => {
              const st = STAGE_STATUS[stage.status] || STAGE_STATUS.not_started;
              const StIcon = st.icon;
              // Map legacy stage ids -> canonical phase keys used by phase_activities
              const STAGE_ID_TO_KEY = {
                inception: 'inception',
                concept: 'concept',
                design_dev: 'design_development',
                design_development: 'design_development',
                documentation: 'documentation',
                construction: 'construction',
                closeout: 'close_out',
                close_out: 'close_out',
              };
              const phaseKey = STAGE_ID_TO_KEY[stage.id] || stage.id;
              const activities = (project.phase_activities || {})[phaseKey] || [];
              const done = activities.filter(a => a.complete).length;
              const total = activities.length;
              return (
                <div key={stage.id} className={`bg-white rounded-sm border border-black/10 p-4 ${stage.status === 'in_progress' ? 'ring-1 ring-blue-200' : ''}`} data-testid={`stage-${stage.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-brand-dark/30 font-body w-5">{i + 1}</span>
                      <StIcon className={`w-4 h-4 ${st.color}`} />
                      <h4 className="font-heading text-sm font-medium">{stage.name}</h4>
                      <Badge className={`text-xs ${st.bg} ${st.color} border-0`}>{st.label}</Badge>
                      {total > 0 && (
                        <span className="text-[11px] text-brand-dark/40 font-body">
                          {done}/{total} activities
                        </span>
                      )}
                    </div>
                    <Select value={stage.status} onValueChange={(v) => updateStage(stage.id, { status: v })}>
                      <SelectTrigger className="w-36 h-8 rounded-sm text-xs" data-testid={`stage-status-${stage.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_started">Not Started</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(stage.notes || stage.started_at || stage.completed_at) && (
                    <div className="mt-2 pl-8 text-xs text-brand-dark/40 font-body space-y-0.5">
                      {stage.notes && <p>{stage.notes}</p>}
                      {stage.started_at && <p>Started: {new Date(stage.started_at).toLocaleDateString()}</p>}
                      {stage.completed_at && <p>Completed: {new Date(stage.completed_at).toLocaleDateString()}</p>}
                    </div>
                  )}
                  {activities.length > 0 && (
                    <div className="mt-3 pl-8 space-y-1.5" data-testid={`stage-activities-${stage.id}`}>
                      {activities.map(a => (
                        <label key={a.key} className="flex items-start gap-2 text-xs font-body cursor-pointer group"
                          data-testid={`activity-${stage.id}-${a.key}`}>
                          <input type="checkbox" checked={!!a.complete}
                            onChange={async (e) => {
                              try {
                                const { data } = await api.patch(`/studio/projects/${projectId}/activity`, {
                                  phase_key: phaseKey, activity_key: a.key, complete: e.target.checked,
                                });
                                project.phase_activities = data.phase_activities;
                                await fetchProject();
                              } catch (err) {
                                console.error('activity toggle failed', err);
                                toast.error('Could not save change. Please retry.');
                              }
                            }}
                            className="mt-0.5 h-3.5 w-3.5 rounded-sm border-brand-dark/20 accent-brand-accent cursor-pointer" />
                          <span className={a.complete ? 'text-brand-dark/40 line-through' : 'text-brand-dark/70 group-hover:text-brand-dark'}>
                            {a.label}
                          </span>
                          {a.complete && a.completed_at && (
                            <span className="ml-auto text-[10px] text-brand-dark/30">
                              {new Date(a.completed_at).toLocaleDateString('en-ZA')}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* SCOPE LOG */}
        <TabsContent value="scope" className="mt-4">
          <div data-testid="scope-log">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-brand-dark/50 font-body">{project.scope_changes?.length || 0} changes logged</p>
              <Button size="sm" onClick={() => setShowScopeForm(!showScopeForm)} data-testid="add-scope-btn"
                className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
                <Plus className="w-3.5 h-3.5 mr-1" /> Log Change
              </Button>
            </div>

            {showScopeForm && (
              <div className="bg-white rounded-sm border border-black/10 p-5 mb-4" data-testid="scope-form">
                <Textarea value={scopeForm.description} onChange={(e) => setScopeForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe the scope change..." className="rounded-sm min-h-[70px] mb-3" data-testid="scope-description" />
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <Select value={scopeForm.impact} onValueChange={(v) => setScopeForm(p => ({ ...p, impact: v }))}>
                    <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="scope-impact"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Impact</SelectItem>
                      <SelectItem value="cost">Cost Impact</SelectItem>
                      <SelectItem value="time">Time Impact</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input value={scopeForm.impact_detail} onChange={(e) => setScopeForm(p => ({ ...p, impact_detail: e.target.value }))}
                    placeholder="Detail (e.g. +R50K)" className="rounded-sm h-9 text-xs" data-testid="scope-detail" />
                  <Input value={scopeForm.requested_by} onChange={(e) => setScopeForm(p => ({ ...p, requested_by: e.target.value }))}
                    placeholder="Requested by" className="rounded-sm h-9 text-xs" data-testid="scope-requestor" />
                </div>
                <Button size="sm" onClick={addScopeChange} data-testid="scope-submit" className="bg-brand-dark text-white rounded-sm text-xs">Save</Button>
              </div>
            )}

            {project.scope_changes?.length === 0 ? (
              <div className="bg-white rounded-sm border border-black/10 p-8 text-center">
                <AlertTriangle className="w-8 h-8 text-brand-dark/15 mx-auto mb-3" />
                <p className="text-brand-dark/40 font-body text-sm">No scope changes logged. Changes will be tracked here with cost and time impact.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {[...(project.scope_changes || [])].reverse().map((change) => (
                  <div key={change.id} className="bg-white rounded-sm border border-black/10 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-body text-brand-dark/80">{change.description}</p>
                      <Badge variant="outline" className={`text-xs capitalize flex-shrink-0 ${IMPACT_COLORS[change.impact]}`}>
                        {change.impact === 'none' ? 'No impact' : `${change.impact} impact`}
                      </Badge>
                    </div>
                    <div className="flex gap-4 mt-2 text-xs text-brand-dark/40 font-body">
                      {change.impact_detail && <span>{change.impact_detail}</span>}
                      <span>By: {change.requested_by}</span>
                      <span>{new Date(change.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* FEES & MILESTONES */}
        <TabsContent value="fees" className="mt-4">
          <div data-testid="fees-section">
            {/* Fee summary */}
            <div className="bg-white rounded-sm border border-black/10 p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50">Fee Structure</h3>
                <Badge variant="outline" className="text-xs capitalize">{project.fee_structure?.fee_type || 'fixed'}</Badge>
              </div>
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-xs text-brand-dark/40 font-body">Total Fee</p>
                  <p className="font-heading text-xl font-medium text-brand-dark">{totalFee ? formatZARFull(totalFee) : 'Not set'}</p>
                </div>
                <Separator orientation="vertical" className="h-10" />
                <div>
                  <p className="text-xs text-brand-dark/40 font-body">Invoiced</p>
                  <p className="font-heading text-lg font-medium text-blue-600">{formatZARFull(totalInvoiced)}</p>
                </div>
                <Separator orientation="vertical" className="h-10" />
                <div>
                  <p className="text-xs text-brand-dark/40 font-body">Collected</p>
                  <p className="font-heading text-lg font-medium text-green-600">{formatZARFull(totalPaid)}</p>
                </div>
                {totalFee > 0 && (
                  <>
                    <Separator orientation="vertical" className="h-10" />
                    <div>
                      <p className="text-xs text-brand-dark/40 font-body">Outstanding</p>
                      <p className="font-heading text-lg font-medium text-amber-600">{formatZARFull(totalFee - totalPaid)}</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Milestones */}
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-brand-dark/50 font-body">{project.milestones?.length || 0} milestones</p>
              <Button size="sm" onClick={() => setShowMilestoneForm(!showMilestoneForm)} data-testid="add-milestone-btn"
                className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Milestone
              </Button>
            </div>

            {showMilestoneForm && (
              <div className="bg-white rounded-sm border border-black/10 p-5 mb-4" data-testid="milestone-form">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <Input value={milestoneForm.description} onChange={(e) => setMilestoneForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Milestone description" className="rounded-sm h-9 text-xs" data-testid="milestone-desc" />
                  <Input type="number" value={milestoneForm.amount} onChange={(e) => setMilestoneForm(p => ({ ...p, amount: e.target.value }))}
                    placeholder="Amount (R)" className="rounded-sm h-9 text-xs" data-testid="milestone-amount" />
                  <Select value={milestoneForm.stage} onValueChange={(v) => setMilestoneForm(p => ({ ...p, stage: v }))}>
                    <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="milestone-stage"><SelectValue placeholder="Link to stage" /></SelectTrigger>
                    <SelectContent>
                      {project.stages?.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="date" value={milestoneForm.due_date} onChange={(e) => setMilestoneForm(p => ({ ...p, due_date: e.target.value }))}
                    className="rounded-sm h-9 text-xs" data-testid="milestone-date" />
                </div>
                <Button size="sm" onClick={addMilestone} data-testid="milestone-submit" className="bg-brand-dark text-white rounded-sm text-xs">Save</Button>
              </div>
            )}

            {project.milestones?.length === 0 ? (
              <div className="bg-white rounded-sm border border-black/10 p-8 text-center">
                <DollarSign className="w-8 h-8 text-brand-dark/15 mx-auto mb-3" />
                <p className="text-brand-dark/40 font-body text-sm">No milestones defined. Add fee milestones linked to project stages.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {(project.milestones || []).map((m) => (
                  <div key={m.id} className="bg-white rounded-sm border border-black/10 p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-body font-medium">{m.description}</p>
                      <div className="flex gap-3 mt-1 text-xs text-brand-dark/40 font-body">
                        {m.stage && <span>{project.stages?.find(s => s.id === m.stage)?.name}</span>}
                        {m.due_date && <span>Due: {m.due_date}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-heading text-sm font-medium">{formatZARFull(m.amount)}</span>
                      <Select value={m.status} onValueChange={(v) => updateMilestone(m.id, v)}>
                        <SelectTrigger className={`w-28 h-8 rounded-sm text-xs ${MILESTONE_STATUS[m.status]}`} data-testid={`milestone-status-${m.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="planned">Planned</SelectItem>
                          <SelectItem value="invoiced">Invoiced</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* BILLING READINESS & INVOICE LOG (C1 + C2) */}
        <TabsContent value="billing" className="mt-4">
          <div className="space-y-6" data-testid="billing-section">
            {/* C1: Billing Readiness Indicators */}
            {billingReadiness && (
              <div>
                <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3 flex items-center gap-2">
                  <Receipt className="w-3.5 h-3.5" /> Billing Readiness
                </h3>
                {billingReadiness.indicators?.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {billingReadiness.indicators.map((ind, i) => (
                      <div key={i} className={`p-3 rounded-sm border ${ind.severity === 'high' ? 'bg-red-50/50 border-red-200' : 'bg-amber-50/50 border-amber-200'}`}>
                        <div className="flex items-start gap-2">
                          <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${ind.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                          <div>
                            <p className="text-sm font-body">{ind.message}</p>
                            {ind.details?.length > 0 && (
                              <div className="mt-1 text-xs text-brand-dark/40">{ind.details.join(', ')}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 bg-green-50 rounded-sm border border-green-100 mb-4">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <p className="text-sm text-green-700 font-body">All billing indicators are clear. No action needed.</p>
                  </div>
                )}
              </div>
            )}

            {/* F: Invoice Generation */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" /> Invoices
                </h3>
                <Button size="sm" onClick={() => setShowInvoiceForm(!showInvoiceForm)} data-testid="generate-invoice-btn"
                  className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Generate Invoice
                </Button>
              </div>

              {showInvoiceForm && (
                <InvoiceGenerator project={project} onClose={() => { setShowInvoiceForm(false); fetchInvoices(); }} />
              )}

              {invoices.filter(i => i.invoice_number).length === 0 && !showInvoiceForm ? (
                <div className="bg-white rounded-sm border border-black/10 p-8 text-center">
                  <Receipt className="w-8 h-8 text-brand-dark/15 mx-auto mb-3" />
                  <p className="text-brand-dark/40 font-body text-sm">No invoices generated. Use "Generate Invoice" to create VAT-inclusive, Waves-compatible invoices.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {invoices.filter(i => i.invoice_number).map((inv) => (
                    <div key={inv.invoice_id} className="bg-white rounded-sm border border-black/10 p-4 flex items-center justify-between" data-testid={`inv-${inv.invoice_number}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-body font-medium">{inv.invoice_number}</p>
                          <Badge variant="outline" className={`text-xs capitalize ${inv.status === 'paid' ? 'bg-green-50 text-green-700 border-green-200' : inv.status === 'issued' ? 'bg-blue-50 text-blue-700 border-blue-200' : inv.status === 'cancelled' ? 'bg-red-50 text-red-500 border-red-200' : 'text-gray-500'}`}>
                            {inv.status}{inv.waves_issued ? ' (Waves)' : ''}
                          </Badge>
                        </div>
                        <p className="text-xs text-brand-dark/40 font-body mt-0.5">
                          {inv.issue_date} · {inv.line_items?.length || 0} item{inv.line_items?.length !== 1 ? 's' : ''}
                          {inv.notes && ` · ${inv.notes.substring(0, 40)}...`}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-heading text-sm font-medium">{formatZARFull(inv.total)}</span>
                        <Select value={inv.status} onValueChange={(v) => updateInvoiceStatus(inv.invoice_id, v)}>
                          <SelectTrigger className="w-28 h-8 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="issued">Issued</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="sm" onClick={() => {
                          const url = `${process.env.REACT_APP_BACKEND_URL}/api/studio/invoices/${inv.invoice_id}/pdf`;
                          fetch(url, { credentials: 'include' }).then(r => r.blob()).then(b => {
                            const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = `${inv.invoice_number}.pdf`; a.click(); URL.revokeObjectURL(u);
                          });
                        }} data-testid={`pdf-${inv.invoice_number}`}><Download className="w-3.5 h-3.5" /></Button>
                        {inv.status === 'draft' && (
                          <Button variant="ghost" size="sm" onClick={() => deleteInvoice(inv.invoice_id)} className="h-8 w-8 p-0">
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* TEAM (C4: Project → Professional Matching) */}
        <TabsContent value="team" className="mt-4">
          <div className="space-y-6" data-testid="team-section">
            {/* Current Team */}
            <div>
              <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3 flex items-center gap-2">
                <Users className="w-3.5 h-3.5" /> Project Team
              </h3>
              {project?.team?.length > 0 ? (
                <div className="space-y-2">
                  {(project.team || []).map((m) => (
                    <div key={m.professional_id} className="bg-white rounded-sm border border-black/10 p-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-body font-medium">{m.name}</p>
                        <p className="text-xs text-brand-dark/40 font-body">{m.discipline}{m.role_notes ? ` · ${m.role_notes}` : ''}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => unlinkProfessional(m.professional_id)} className="h-8 w-8 p-0" data-testid={`unlink-${m.professional_id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-sm border border-black/10 p-6 text-center mb-4">
                  <Users className="w-8 h-8 text-brand-dark/15 mx-auto mb-2" />
                  <p className="text-brand-dark/40 font-body text-sm">No professionals linked. Add team members from suggestions below.</p>
                </div>
              )}
            </div>

            {/* Suggested Professionals */}
            <div>
              <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3">Suggested Professionals</h3>
              <p className="text-xs text-brand-dark/30 font-body mb-3">Based on project region, type, and professional availability. You decide who to add.</p>
              {suggestedProfs.length === 0 ? (
                <div className="bg-white rounded-sm border border-black/10 p-6 text-center">
                  <p className="text-brand-dark/30 font-body text-sm">No professionals in the network yet. Add professionals via the Network tab.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {suggestedProfs.map((prof) => {
                    const alreadyLinked = project?.team?.some(t => t.professional_id === prof.professional_id);
                    return (
                      <div key={prof.professional_id} className={`bg-white rounded-sm border p-4 ${alreadyLinked ? 'border-green-200 bg-green-50/20' : 'border-black/10'}`}
                        data-testid={`suggested-${prof.professional_id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-body font-medium">{prof.name}</p>
                              <Badge variant="outline" className="text-xs capitalize">{prof.discipline}</Badge>
                              {prof.availability === 'available' && <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Available</Badge>}
                              {alreadyLinked && <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">On Team</Badge>}
                            </div>
                            <p className="text-xs text-brand-dark/40 font-body mt-0.5">
                              {prof.firm && `${prof.firm} · `}{prof.regions?.join(', ')}
                              {prof.match_reasons?.length > 0 && ` · ${prof.match_reasons.join(', ')}`}
                            </p>
                          </div>
                          {!alreadyLinked && (
                            <Button size="sm" onClick={() => linkProfessional(prof)} data-testid={`link-${prof.professional_id}`}
                              className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm text-xs">
                              <UserPlus className="w-3.5 h-3.5 mr-1" /> Add
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* BIM INTELLIGENCE (Phase E) */}
        <TabsContent value="bim" className="mt-4">
          <div className="space-y-6" data-testid="bim-section">
            {/* Upload */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brand-dark/50 font-body">{bimSnapshots.length} snapshot{bimSnapshots.length !== 1 ? 's' : ''}</p>
              </div>
              <Button size="sm" onClick={() => setShowBimForm(!showBimForm)} data-testid="add-snapshot-btn"
                className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
                <Upload className="w-3.5 h-3.5 mr-1" /> Upload Snapshot
              </Button>
            </div>

            {showBimForm && (
              <div className="bg-white rounded-sm border border-black/10 p-5" data-testid="bim-upload-form">
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark/60 mb-4">Revit Metadata Snapshot</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Declared Stage *</label>
                    <Select value={bimForm.declared_stage} onValueChange={(v) => setBimForm(p => ({ ...p, declared_stage: v }))}>
                      <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="bim-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
                      <SelectContent>
                        {['Concept', 'Schematic', 'Design Development', 'Technical', 'Construction', 'Site Phase'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Label *</label>
                    <Select value={bimForm.label} onValueChange={(v) => setBimForm(p => ({ ...p, label: v }))}>
                      <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="bim-label"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['baseline', 'checkpoint', 'pre-submission', 'issue-response'].map(l => <SelectItem key={l} value={l}>{l.replace('-', ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input value={bimForm.gfa} onChange={(e) => setBimForm(p => ({ ...p, gfa: e.target.value }))} placeholder="GFA (m²) *" type="number" className="rounded-sm h-9 text-xs" data-testid="bim-gfa" />
                  <Input value={bimForm.levels} onChange={(e) => setBimForm(p => ({ ...p, levels: e.target.value }))} placeholder="Levels" type="number" className="rounded-sm h-9 text-xs" />
                  <Input value={bimForm.room_count} onChange={(e) => setBimForm(p => ({ ...p, room_count: e.target.value }))} placeholder="Room count" type="number" className="rounded-sm h-9 text-xs" />
                  <Input value={bimForm.element_count} onChange={(e) => setBimForm(p => ({ ...p, element_count: e.target.value }))} placeholder="Element count" type="number" className="rounded-sm h-9 text-xs" />
                  <Input value={bimForm.wall_area} onChange={(e) => setBimForm(p => ({ ...p, wall_area: e.target.value }))} placeholder="Wall area (m²)" type="number" className="rounded-sm h-9 text-xs" />
                  <Input value={bimForm.roof_area} onChange={(e) => setBimForm(p => ({ ...p, roof_area: e.target.value }))} placeholder="Roof area (m²)" type="number" className="rounded-sm h-9 text-xs" />
                  <Input value={bimForm.glazing_area} onChange={(e) => setBimForm(p => ({ ...p, glazing_area: e.target.value }))} placeholder="Glazing area (m²)" type="number" className="rounded-sm h-9 text-xs" />
                  <Input value={bimForm.revit_file} onChange={(e) => setBimForm(p => ({ ...p, revit_file: e.target.value }))} placeholder="Revit file name" className="rounded-sm h-9 text-xs" />
                  <Input value={bimForm.revit_version} onChange={(e) => setBimForm(p => ({ ...p, revit_version: e.target.value }))} placeholder="Revit version" className="rounded-sm h-9 text-xs" />
                </div>
                <Textarea value={bimForm.notes} onChange={(e) => setBimForm(p => ({ ...p, notes: e.target.value }))} placeholder="Snapshot notes (optional)" className="rounded-sm min-h-[50px] text-xs mb-3" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={uploadSnapshot} className="bg-brand-dark text-white rounded-sm text-xs" data-testid="bim-submit">Upload Snapshot</Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowBimForm(false)}>Cancel</Button>
                </div>
              </div>
            )}

            {/* Signals */}
            {bimSignals?.has_bim && bimSignals.signals?.length > 0 && (
              <div>
                <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3 flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5" /> BIM Intelligence Signals
                </h3>
                <div className="space-y-2">
                  {bimSignals.signals.map((sig, i) => (
                    <div key={i} className={`p-3 rounded-sm border ${sig.severity === 'high' ? 'bg-red-50/50 border-red-200' : 'bg-amber-50/50 border-amber-200'}`} data-testid={`bim-signal-${i}`}>
                      <div className="flex items-start gap-2">
                        <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${sig.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                        <div>
                          <p className="text-sm font-body">{sig.message}</p>
                          <Badge variant="outline" className="text-[10px] mt-1 capitalize">{sig.category}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {bimSignals?.has_bim && bimSignals.signals?.length === 0 && (
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-sm border border-green-100">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <p className="text-sm text-green-700 font-body">No BIM intelligence signals. Model aligns with project assumptions.</p>
              </div>
            )}

            {/* Summary (deltas, not raw) */}
            {bimSignals?.has_bim && bimSignals.summary && (
              <div className="bg-white rounded-sm border border-black/10 p-5">
                <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3">Model Overview</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-brand-dark/40">Latest GFA</p>
                    <p className="text-sm font-body font-medium">{bimSignals.summary.latest_gfa} m²</p>
                    {bimSignals.summary.baseline_gfa > 0 && bimSignals.summary.baseline_gfa !== bimSignals.summary.latest_gfa && (
                      <p className={`text-xs font-body ${bimSignals.summary.latest_gfa > bimSignals.summary.baseline_gfa ? 'text-red-500' : 'text-green-600'}`}>
                        {((bimSignals.summary.latest_gfa - bimSignals.summary.baseline_gfa) / bimSignals.summary.baseline_gfa * 100).toFixed(0)}% vs baseline
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-brand-dark/40">Levels</p>
                    <p className="text-sm font-body font-medium">{bimSignals.summary.latest_levels}</p>
                  </div>
                  <div>
                    <p className="text-xs text-brand-dark/40">Rooms</p>
                    <p className="text-sm font-body font-medium">{bimSignals.summary.latest_rooms}</p>
                  </div>
                  <div>
                    <p className="text-xs text-brand-dark/40">Declared Stage</p>
                    <p className="text-sm font-body font-medium">{bimSignals.summary.latest_declared_stage}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Snapshot History */}
            {bimSnapshots.length > 0 && (
              <div>
                <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3">Snapshot History</h3>
                <div className="space-y-2">
                  {[...bimSnapshots].reverse().map((snap) => (
                    <div key={snap.snapshot_id} className="bg-white rounded-sm border border-black/10 p-3" data-testid={`snapshot-${snap.snapshot_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs capitalize">{snap.label.replace('-', ' ')}</Badge>
                          <Badge variant="outline" className="text-xs">{snap.declared_stage}</Badge>
                          <span className="text-xs text-brand-dark/40 font-body">{snap.gfa} m² GFA</span>
                        </div>
                        <span className="text-xs text-brand-dark/30 font-body">{new Date(snap.created_at).toLocaleDateString()}</span>
                      </div>
                      {snap.notes && <p className="text-xs text-brand-dark/40 font-body mt-1">{snap.notes}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!bimSignals?.has_bim && bimSnapshots.length === 0 && (
              <div className="bg-white rounded-sm border border-black/10 p-8 text-center">
                <Upload className="w-8 h-8 text-brand-dark/15 mx-auto mb-3" />
                <h3 className="font-heading text-lg font-medium text-brand-dark mb-2">No BIM data yet</h3>
                <p className="text-brand-dark/40 font-body text-sm max-w-md mx-auto">
                  Upload a Revit metadata snapshot to enable scope drift detection, stage alignment checks, and fee risk intelligence.
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* CONTEXT (Carried-over data) */}
        <TabsContent value="context" className="mt-4">
          <div className="space-y-4" data-testid="context-section">
            {project.budget_assumptions && (
              <div className="bg-white rounded-sm border border-black/10 p-5">
                <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3">Budget Assumptions (from Feasibility)</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div><p className="text-xs text-brand-dark/40">Budget</p><p className="text-sm font-body font-medium">{formatZARFull(project.budget_assumptions.budget || 0)}</p></div>
                  <div><p className="text-xs text-brand-dark/40">Cost Range</p><p className="text-sm font-body font-medium">{formatZARFull(project.budget_assumptions.cost_range?.min || 0)} - {formatZARFull(project.budget_assumptions.cost_range?.max || 0)}</p></div>
                  <div><p className="text-xs text-brand-dark/40">Finish Level</p><p className="text-sm font-body font-medium capitalize">{project.budget_assumptions.finish_level || 'N/A'}</p></div>
                </div>
              </div>
            )}

            {project.architect_summary && (
              <div className="bg-white rounded-sm border border-black/10 p-5">
                <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3">Architect Summary (from Guided Consultation)</h3>
                <div className="text-sm font-body text-brand-dark/70 leading-relaxed whitespace-pre-wrap">{project.architect_summary}</div>
              </div>
            )}

            {project.brief && (
              <div className="bg-white rounded-sm border border-black/10 p-5">
                <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3">Project Brief</h3>
                <div className="grid grid-cols-2 gap-3 text-sm font-body">
                  {project.brief.bedrooms && <div><span className="text-brand-dark/40">Bedrooms:</span> {project.brief.bedrooms}</div>}
                  {project.brief.bathrooms && <div><span className="text-brand-dark/40">Bathrooms:</span> {project.brief.bathrooms}</div>}
                  {project.brief.budget_range && <div><span className="text-brand-dark/40">Budget Range:</span> {project.brief.budget_range}</div>}
                  {project.brief.timeline && <div><span className="text-brand-dark/40">Timeline:</span> {project.brief.timeline}</div>}
                  {project.brief.style_preferences?.length > 0 && <div className="col-span-2"><span className="text-brand-dark/40">Style:</span> {project.brief.style_preferences.join(', ')}</div>}
                  {project.brief.priorities?.length > 0 && <div className="col-span-2"><span className="text-brand-dark/40">Priorities:</span> {project.brief.priorities.join(', ')}</div>}
                  {project.brief.special_spaces?.length > 0 && <div className="col-span-2"><span className="text-brand-dark/40">Special Spaces:</span> {project.brief.special_spaces.join(', ')}</div>}
                </div>
              </div>
            )}

            {project.fit_score && (
              <div className="bg-white rounded-sm border border-black/10 p-5">
                <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3">Fit Score</h3>
                <p className="font-heading text-lg font-medium">Score: {project.fit_score.score || 'N/A'}</p>
              </div>
            )}

            {!project.budget_assumptions && !project.architect_summary && !project.brief && !project.fit_score && (
              <div className="bg-white rounded-sm border border-black/10 p-8 text-center">
                <FileText className="w-8 h-8 text-brand-dark/15 mx-auto mb-3" />
                <p className="text-brand-dark/40 font-body text-sm">No pre-design context was carried over for this project.</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* NOTES */}
        <TabsContent value="notes" className="mt-4">
          <div data-testid="notes-section">
            <div className="flex gap-2 mb-4">
              <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add an internal note..." className="rounded-sm min-h-[60px]" data-testid="workspace-note-input" />
              <Button onClick={addNote} className="bg-brand-dark text-white rounded-sm self-end" size="sm" data-testid="workspace-note-submit">
                <Send className="w-4 h-4" />
              </Button>
            </div>
            {project.internal_notes?.length === 0 ? (
              <p className="text-brand-dark/30 text-sm font-body text-center py-8">No internal notes yet.</p>
            ) : (
              <div className="space-y-2">
                {[...(project.internal_notes || [])].reverse().map((n, i) => (
                  <div key={i} className="bg-white rounded-sm border border-black/10 p-4">
                    <p className="text-sm font-body text-brand-dark/80">{n.content}</p>
                    <p className="text-xs text-brand-dark/30 mt-1">{n.author} — {new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}


function InvoiceGenerator({ project, onClose }) {
  const [lines, setLines] = useState([]);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const addLine = (type, desc = '', qty = 1, rate = 0, disc = 0) => {
    setLines(prev => [...prev, { id: crypto.randomUUID(), type, description: desc, quantity: qty, unit_rate: rate, discount_pct: disc }]);
  };

  const updateLine = (id, field, value) => {
    setLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const removeLine = (id) => setLines(prev => prev.filter(l => l.id !== id));

  const lineTotal = (l) => {
    const t = l.quantity * l.unit_rate * (1 - (l.discount_pct || 0) / 100);
    return Math.round(t * 100) / 100;
  };

  const subtotal = lines.reduce((sum, l) => sum + lineTotal(l), 0);
  const vatAmount = Math.round(subtotal * 0.15 * 100) / 100;
  const total = Math.round((subtotal + vatAmount) * 100) / 100;

  const addFromMilestone = (m) => {
    const stage = project.stages?.find(s => s.id === m.stage);
    const desc = stage ? `${stage.name} - ${m.description}` : m.description;
    addLine('stage', desc, 1, m.amount, 0);
  };

  const submit = async () => {
    if (lines.length === 0) { toast.error('Add at least one line item'); return; }
    setLoading(true);
    try {
      const payload = {
        issue_date: issueDate, due_date: dueDate, notes,
        line_items: lines.map(l => ({ description: l.description, quantity: Number(l.quantity), unit_rate: Number(l.unit_rate), discount_pct: Number(l.discount_pct || 0), type: l.type })),
      };
      const { data } = await api.post(`/studio/projects/${project.project_id}/invoices`, payload);
      toast.success(`Draft ${data.invoice_number} created`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create invoice'); }
    finally { setLoading(false); }
  };

  const eligibleMilestones = project.milestones?.filter(m => m.status === 'planned' || m.status === 'invoiced') || [];

  return (
    <div className="bg-white rounded-sm border border-black/10 p-5 mb-4" data-testid="invoice-generator">
      <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark/60 mb-4">Generate Invoice (Draft)</h3>

      {/* Quick-add from milestones */}
      {eligibleMilestones.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-brand-dark/40 font-body mb-2">Add from milestones:</p>
          <div className="flex flex-wrap gap-2">
            {eligibleMilestones.map(m => (
              <Button key={m.id} variant="outline" size="sm" onClick={() => addFromMilestone(m)}
                className="text-xs rounded-sm h-7" data-testid={`add-milestone-${m.id}`}>
                + {m.description} ({formatZARFull(m.amount)})
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Manual line buttons */}
      <div className="flex gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={() => addLine('stage')} className="text-xs rounded-sm h-7" data-testid="add-stage-line">+ Stage Line</Button>
        <Button variant="outline" size="sm" onClick={() => addLine('time', '', 1, 0, 0)} className="text-xs rounded-sm h-7" data-testid="add-time-line">+ Time Line</Button>
        <Button variant="outline" size="sm" onClick={() => addLine('disbursement')} className="text-xs rounded-sm h-7" data-testid="add-disbursement-line">+ Disbursement</Button>
        <Button variant="outline" size="sm" onClick={() => addLine('previous_payment', 'Less: Previous payment', 1, 0, 0)} className="text-xs rounded-sm h-7" data-testid="add-payment-line">+ Less Payment</Button>
      </div>

      {/* Line items */}
      {lines.length > 0 && (
        <div className="mb-4 space-y-2">
          <div className="grid grid-cols-12 gap-2 text-[10px] font-heading uppercase tracking-wider text-brand-dark/40 px-1">
            <div className="col-span-5">Description</div>
            <div className="col-span-2">Qty</div>
            <div className="col-span-2">Rate (R)</div>
            <div className="col-span-1">Disc%</div>
            <div className="col-span-1 text-right">Total</div>
            <div className="col-span-1"></div>
          </div>
          {lines.map(l => (
            <div key={l.id} className={`grid grid-cols-12 gap-2 items-center ${l.type === 'previous_payment' ? 'bg-red-50/30 p-1 rounded-sm' : ''}`}>
              <Input className="col-span-5 rounded-sm h-8 text-xs" value={l.description} onChange={e => updateLine(l.id, 'description', e.target.value)} placeholder="Description" />
              <Input className="col-span-2 rounded-sm h-8 text-xs" type="number" value={l.quantity} onChange={e => updateLine(l.id, 'quantity', e.target.value)} />
              <Input className="col-span-2 rounded-sm h-8 text-xs" type="number" value={l.unit_rate} onChange={e => updateLine(l.id, 'unit_rate', e.target.value)} />
              <Input className="col-span-1 rounded-sm h-8 text-xs" type="number" value={l.discount_pct || ''} onChange={e => updateLine(l.id, 'discount_pct', e.target.value)} placeholder="0" />
              <span className={`col-span-1 text-xs font-body text-right font-medium ${lineTotal(l) < 0 ? 'text-red-600' : ''}`}>
                {formatZARFull(lineTotal(l))}
              </span>
              <Button variant="ghost" size="sm" onClick={() => removeLine(l.id)} className="col-span-1 h-8 w-8 p-0">
                <Trash2 className="w-3 h-3 text-red-400" />
              </Button>
            </div>
          ))}

          {/* Totals */}
          <div className="border-t border-black/10 pt-3 mt-2">
            <div className="flex justify-end gap-8 text-sm font-body">
              <div className="text-right space-y-1">
                <p className="text-brand-dark/50">Subtotal</p>
                <p className="text-brand-dark/50">VAT @ 15%</p>
                <p className="font-heading font-bold text-brand-dark text-base">Total (ZAR)</p>
              </div>
              <div className="text-right space-y-1 min-w-[100px]">
                <p>{formatZARFull(subtotal)}</p>
                <p>{formatZARFull(vatAmount)}</p>
                <p className="font-heading font-bold text-brand-dark text-base">{formatZARFull(total)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dates + Notes */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/50 block mb-1">Issue Date</label>
          <Input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} className="rounded-sm h-9 text-xs" data-testid="inv-issue-date" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/50 block mb-1">Due Date</label>
          <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="rounded-sm h-9 text-xs" data-testid="inv-due-date" />
        </div>
      </div>
      <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes / terms (optional)" className="rounded-sm min-h-[50px] text-xs mb-3" />

      <div className="flex gap-2">
        <Button onClick={submit} disabled={loading || lines.length === 0} className="bg-brand-dark text-white rounded-sm text-xs uppercase tracking-wider" data-testid="inv-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Draft Invoice'}
        </Button>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}
